import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useForm, useFormState } from 'react-final-form';
import {
  AutocompleteInput,
  AutocompleteArrayInput,
  ReferenceInput,
  ReferenceArrayInput,
  useDataProvider,
} from 'react-admin';
import {
  Card,
  CardContent,
  Chip,
  IconButton,
  makeStyles,
  Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { removeSeconds } from '../field/TimeField';

const useSelectedCleanerCardStyles = makeStyles((theme) => {
  return {
    root: {
      position: 'relative',
      marginBottom: theme.spacing(2),
      maxWidth: '15rem',
      // Lets the card stretch to match the height of its sibling (the
      // contract card) when both sit in a flex row with align-items:
      // stretch — see ContractAndCleanerSelectBlock in AffectationForm.js.
      height: '100%',
      boxSizing: 'border-box',
      // Softens the placeholder <-> filled-card transition (border colour,
      // height as content changes) within the card's own lifetime — see
      // the same comment on ContractAutocompleteInput's root class for why
      // this can't (with a plain CSS transition) also animate the
      // autocomplete <-> card swap itself.
      transition: 'all 0.15s',
      // Neutral fallback border for an unrecognized/loading status —
      // overridden below (rootSalariéActuel/rootAncienSalarié/
      // rootVivierSalarial) by the matching status colour so the whole
      // card's outline reflects employee_status, not just the chip. Same
      // convention as ContractAutocompleteInput's rootRécurrent/rootPonctuel.
      border: `1px solid ${theme.palette.divider}`,
    },
    // Card-wide border variants, keyed the same way as the chip colour
    // classes below (employee_status, normalized).
    rootSalariéActuel: {
      border: `2px solid ${theme.palette.status.success}`,
    },
    rootAncienSalarié: {
      border: `2px solid ${theme.palette.status.cancelled}`,
    },
    rootVivierSalarial: {
      border: `2px solid ${theme.palette.primary.main}`,
    },
    closeButton: {
      position: 'absolute',
      top: theme.spacing(0.5),
      right: theme.spacing(0.5),
    },
    fullname: {
      fontWeight: 500,
      paddingRight: theme.spacing(4),
    },
    manager: {
      color: theme.palette.text.secondary,
    },
    chip: {
      marginTop: theme.spacing(1),
    },
    // employee_status -> Chip colour, same values as the card border above
    // (theme.palette.status.success/cancelled, already used elsewhere for
    // "active/ok" vs "closed/neutral" states — e.g.
    // DirectDebitStatusChipField, InvoiceStatusChipField;
    // theme.palette.primary.main for "Vivier salarial", the repo's own blue
    // brand colour, already the default MUI `color="primary"` everywhere).
    salariéActuel: {
      backgroundColor: theme.palette.status.success,
      color: theme.palette.getContrastText(theme.palette.status.success),
    },
    ancienSalarié: {
      backgroundColor: theme.palette.status.cancelled,
      color: theme.palette.getContrastText(theme.palette.status.cancelled),
    },
    vivierSalarial: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    workContract: {
      marginTop: theme.spacing(0.5),
      color: theme.palette.text.secondary,
    },
    placeholder: {
      borderStyle: 'dashed',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    placeholderText: {
      color: theme.palette.text.disabled,
      fontStyle: 'italic',
      textAlign: 'center',
    },
  };
});

// Restricts the choice list to cleaners currently on staff — see
// Cleaner/detail/Filter.js (CleanerTypeSelectInput, `employee_status`
// initialValue="Salarié actuel") for the same convention on the Cleaner
// list. Column confirmed on care.cleaner (care_v1.2 sql/views.sql
// cleaner_core, contact.employee_status__c).
export const ACTIVE_CLEANER_FILTER = { employee_status: 'Salarié actuel' };

// employee_status (normalized) -> the Card-root border / Chip colour class
// name pair above. The three values come from CleanerTypeSelectInput.js
// (components/input/CleanerTypeSelectInput.js): 'Salarié actuel', 'Ancien
// salarié', 'Vivier salarial'.
const EMPLOYEE_STATUS_CLASS = {
  'salarié actuel': { root: 'rootSalariéActuel', chip: 'salariéActuel' },
  'ancien salarié': { root: 'rootAncienSalarié', chip: 'ancienSalarié' },
  'vivier salarial': { root: 'rootVivierSalarial', chip: 'vivierSalarial' },
};

// employee_status arrives via Salesforce/PostgREST JSON — normalize (trim +
// NFC + case-fold) before matching, same rationale/approach as
// ContractAutocompleteInput's normalizeContractType (a stray trailing space
// or an NFD-decomposed accent shouldn't silently fall through to the
// default styling).
const normalizeEmployeeStatus = (status) => {
  return (status || '')
    .normalize('NFC')
    .trim()
    .toLowerCase();
};

const getEmployeeStatusClasses = (status) => {
  return EMPLOYEE_STATUS_CLASS[normalizeEmployeeStatus(status)] || {};
};

// Renders the cleaner's own current work contract (hours/week, type, start
// date) — NOT the affectation's selected `care.contract` (a different
// entity, the account-side contract). Confirmed on care.cleaner (care_v1.2
// sql/views.sql cleaner_core: contract_terms.* — contract_type, hourly_rate,
// contract_worked_time [an interval, "HH:MM:SS" over PostgREST — reuses
// TimeField's removeSeconds for the "HH:MM" display already used elsewhere,
// e.g. Affectation/detail/Aside/CleanerInformation.js], last_terms_start_date).
// No end date is exposed under this alias — the underlying join already
// filters to contracts with no end date or a future one, so none is shown.
const CleanerWorkContractInfo = ({ cleaner, classes }) => {
  if (!cleaner || !cleaner.contract_type) {
    return null;
  }
  return (
    <Typography variant="body2" className={classes.workContract}>
      {cleaner.contract_type}
      {cleaner.contract_worked_time &&
        ` - ${removeSeconds(cleaner.contract_worked_time)}/sem.`}
      {cleaner.last_terms_start_date &&
        ` - depuis ${cleaner.last_terms_start_date}`}
    </Typography>
  );
};

CleanerWorkContractInfo.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  cleaner: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  classes: PropTypes.object.isRequired,
};

CleanerWorkContractInfo.defaultProps = {
  cleaner: null,
};

// Read-only recap of the selected cleaner, shown instead of the autocomplete
// once a `cleaner_id` is picked (see `showSelectedAsCard`). Mirrors
// SelectedContractCard (ContractAutocompleteInput.js): same data-fetch
// pattern (useDataProvider + useEffect, form only holds the id) and same
// close-button-resets-the-field UX. Only meaningful for the singular
// (non-multiple) selection mode.
const SelectedCleanerCard = ({ cleanerId, onReset }) => {
  const classes = useSelectedCleanerCardStyles();
  const dataProvider = useDataProvider();
  const [cleaner, setCleaner] = useState(null);

  useEffect(() => {
    let cancelled = false;
    dataProvider
      .getOne('cleaner', { id: cleanerId })
      .then(({ data }) => {
        if (!cancelled) {
          setCleaner(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCleaner(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [cleanerId, dataProvider]);

  const statusClasses = cleaner
    ? getEmployeeStatusClasses(cleaner.employee_status)
    : {};

  return (
    <Card
      variant="outlined"
      className={clsx(classes.root, classes[statusClasses.root])}
    >
      <IconButton
        size="small"
        className={classes.closeButton}
        onClick={onReset}
        aria-label="Changer de cleaner"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <CardContent>
        <Typography variant="body1" className={classes.fullname}>
          {cleaner ? cleaner.fullname : '…'}
        </Typography>
        {cleaner && cleaner.manager_fullname && (
          <Typography variant="body2" className={classes.manager}>
            Manager : {cleaner.manager_fullname}
          </Typography>
        )}
        {cleaner && (
          <Chip
            size="small"
            className={clsx(classes.chip, classes[statusClasses.chip])}
            label={cleaner.employee_status}
          />
        )}
        <CleanerWorkContractInfo cleaner={cleaner} classes={classes} />
      </CardContent>
    </Card>
  );
};

SelectedCleanerCard.propTypes = {
  cleanerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onReset: PropTypes.func.isRequired,
};

// Sober placeholder for the "top" slot (see `slot` prop) when nothing is
// selected yet — same card footprint (width/height) as SelectedCleanerCard,
// dashed border, so the two-slot row (see ContractAndCleanerSelectBlock in
// AffectationForm.js) never has a visually broken/empty cell while the
// actual autocomplete has moved down to the row below.
const EmptyCleanerCardPlaceholder = () => {
  const classes = useSelectedCleanerCardStyles();
  return (
    <Card
      variant="outlined"
      className={clsx(classes.root, classes.placeholder)}
    >
      <Typography variant="body2" className={classes.placeholderText}>
        Aucun cleaner sélectionné
      </Typography>
    </Card>
  );
};

const CleanerAutocompleteInput = ({
  bottomSlotStyle,
  defaultValue,
  filter,
  fullWidth,
  helperText,
  label,
  multiple,
  onlyActive,
  resource,
  showSelectedAsCard,
  slot,
  source,
  validate,
  ...props
}) => {
  const form = useForm();
  const { values } = useFormState();

  const mergedFilter = {
    ...filter,
    ...(onlyActive ? ACTIVE_CLEANER_FILTER : {}),
  };

  const singularSource = source || 'cleaner_id';

  // Entry points that still pre-fill the plural `cleaner_ids` (e.g.
  // Cleaner/detail/AffectationTab/AffectationCard/Actions.js: the "create
  // affectation" button on a cleaner's own page navigates via react-router
  // `state: { record: { cleaner_ids: [record.id], ... } }`, read natively by
  // react-admin's useCreateController — see ra-core getRecord — into this
  // form's initial `record`/values) would otherwise leave `cleaner_id` empty
  // even though a cleaner is already known. Fall back to the first
  // `cleaner_ids` entry for the initial render (so the card shows on load,
  // no flash of the empty autocomplete), and migrate it into the singular
  // field once so the rest of this component (which only reads/writes
  // `singularSource` from here on, e.g. the reset button) stays consistent.
  const prefilledCleanerId =
    values[singularSource] ||
    (values.cleaner_ids && values.cleaner_ids.length
      ? values.cleaner_ids[0]
      : undefined);

  useEffect(() => {
    if (
      !multiple &&
      showSelectedAsCard &&
      !values[singularSource] &&
      prefilledCleanerId
    ) {
      form.change(singularSource, prefilledCleanerId);
    }
    // Only ever run this migration once, on mount — not on every keystroke
    // of the (now-singular) autocomplete, which also writes to
    // `singularSource` and must not be fought by this effect afterwards.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSelected =
    !multiple && showSelectedAsCard && Boolean(prefilledCleanerId);

  const card = (
    <SelectedCleanerCard
      cleanerId={prefilledCleanerId}
      onReset={() => {
        form.change(singularSource, null);
        // Also clear the plural field the value may have come from (see
        // `prefilledCleanerId` above) — otherwise it would immediately
        // resurface as the "prefilled" id on the next render, making the
        // reset button a no-op from the user's point of view.
        if (values.cleaner_ids) {
          form.change('cleaner_ids', null);
        }
      }}
    />
  );

  const autocomplete = multiple ? (
    <ReferenceArrayInput
      {...props}
      defaultValue={defaultValue}
      filterToQuery={
        /* istanbul ignore next */
        (searchText) => {
          return { fullname: searchText };
        }
      }
      sort={{ field: 'fullname', order: 'ASC' }}
      filter={mergedFilter}
      source={source || 'cleaner_ids'}
      reference="cleaner"
      resource={resource}
      allowEmpty
      validate={validate}
      options={{ fullWidth }}
      label={label}
      autoComplete="off"
    >
      <AutocompleteArrayInput optionText="fullname" helperText={helperText} />
    </ReferenceArrayInput>
  ) : (
    <ReferenceInput
      defaultValue={defaultValue}
      filterToQuery={
        /* istanbul ignore next */
        (searchText) => {
          return { fullname: searchText };
        }
      }
      sort={{ field: 'fullname', order: 'ASC' }}
      filter={mergedFilter}
      source={singularSource}
      reference="cleaner"
      resource={resource}
      allowEmpty
      emptyText="ra.action.clear_input_value"
      validate={validate}
      label={label}
    >
      <AutocompleteInput
        optionText={(cleaner) => {
          // When `onlyActive` filters the list down to `employee_status ===
          // 'Salarié actuel'` (see ACTIVE_CLEANER_FILTER), every result is in
          // that same state — showing it on each row is redundant. It only
          // becomes informative again once the filter is off (`show_all`
          // checked) and results mix current/former/applicant statuses.
          return onlyActive
            ? cleaner?.fullname
            : `${cleaner?.fullname} (${cleaner?.employee_status})`;
        }}
        options={{ fullWidth }}
        helperText={helperText}
      />
    </ReferenceInput>
  );

  // Split rendering: the caller (ContractAndCleanerSelectBlock in
  // AffectationForm.js) renders this component twice, once per slot, to
  // build a fixed "results" row (top: card or placeholder, always present)
  // above a "still to fill" row (bottom: the active autocompletes for
  // whichever slots aren't picked yet). Other callers don't pass `slot`, so
  // they keep the single-spot bascule behaviour (card replaces the
  // autocomplete in place) unchanged. Multi-select has no "one card"
  // equivalent, so its "top" slot is empty and its "bottom" slot is always
  // the (multi) autocomplete.
  if (slot === 'top') {
    return isSelected ? card : <EmptyCleanerCardPlaceholder />;
  }
  if (slot === 'bottom') {
    // Once selected, render nothing at all — not even an empty wrapper div.
    // A caller-provided `bottomSlotStyle` (width cap, etc.) applied
    // unconditionally in the parent (e.g. a <div style={...}> around this
    // component) would still count as a flex item in the "still to fill"
    // row even once empty, breaking justify-content: flex-start for
    // whichever input remains (it would sit after this invisible spacer,
    // offset by the row's gap, instead of flush left). Owning the wrapper
    // here — only emitted alongside real content — avoids that.
    if (isSelected) {
      return null;
    }
    return bottomSlotStyle ? (
      <div style={bottomSlotStyle}>{autocomplete}</div>
    ) : (
      autocomplete
    );
  }

  // Opt-in, singular-selection only (a multi-select doesn't have a natural
  // "one card" equivalent — AutocompleteArrayInput already renders its own
  // chips for each pick). See SelectedContractCard for the same pattern.
  if (isSelected) {
    return card;
  }

  return autocomplete;
};

CleanerAutocompleteInput.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  bottomSlotStyle: PropTypes.object,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  filter: PropTypes.object,
  fullWidth: PropTypes.bool,
  helperText: PropTypes.bool,
  label: PropTypes.string,
  multiple: PropTypes.bool,
  onlyActive: PropTypes.bool,
  resource: PropTypes.string,
  showSelectedAsCard: PropTypes.bool,
  slot: PropTypes.oneOf(['top', 'bottom']),
  source: PropTypes.string,
  validate: PropTypes.oneOfType([PropTypes.func, PropTypes.array]),
};

CleanerAutocompleteInput.defaultProps = {
  bottomSlotStyle: undefined,
  defaultValue: undefined,
  filter: {},
  fullWidth: true,
  helperText: undefined,
  label: undefined,
  multiple: false,
  onlyActive: false,
  resource: '',
  showSelectedAsCard: false,
  slot: undefined,
  source: '',
  validate: /* istanbul ignore next */ () => {},
};

export default CleanerAutocompleteInput;
